import './styles/global.css';
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Nav from './components/Nav';
import ScrollToTop from './components/ScrollToTop';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import ErrorBoundary from './components/ErrorBoundary';

import Home        from './pages/Home';
import About       from './pages/About';
import FAQs        from './pages/FAQs';
import Profile     from './pages/Profile';
import Admin       from './pages/Admin';
import { Login, Register, ForgotPassword, ResetPassword } from './pages/Auth';
import VerifyEmail from './pages/VerifyEmail';
import { TrailersList, TrailerDetail } from './pages/Trailers';
import {
  Library, HowItWorks, Standings, Events, Merch,
  Contact, Dashboard, WalletPage, NotFound,
} from './pages/Pages';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  return user ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

function AppLayout() {
  return (
    <div className="app-shell">
      <Nav />
      {/* Sidebar spacer renders on every page now — homepage used to skip this
          because the filmstrip offset was handled in Home.module.css. H14 retired
          the filmstrip and unified the layout across all pages. */}
      <div className="sidebar-spacer" />
      <main className="main-content">
        <ScrollToTop />
        <ErrorBoundary>
          <Routes>
            <Route path="/"               element={<Home />} />
            <Route path="/about"          element={<About />} />
            <Route path="/how-it-works"   element={<HowItWorks />} />
            <Route path="/faqs"           element={<FAQs />} />
            <Route path="/contact"        element={<Contact />} />
            <Route path="/standings"      element={<Standings />} />
            <Route path="/library"        element={<Library />} />
            <Route path="/events"         element={<Events />} />
            <Route path="/merch"          element={<Merch />} />
            <Route path="/trailers"       element={<TrailersList />} />
            <Route path="/trailers/:slug" element={<TrailerDetail />} />
            <Route path="/login"           element={<Login />} />
            <Route path="/register"        element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password"  element={<ResetPassword />} />
            <Route path="/verify-email"    element={<VerifyEmail />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/wallet"    element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
            <Route path="/profile"   element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/admin"     element={<AdminRoute><Admin /></AdminRoute>} />
            <Route path="*"          element={<NotFound />} />
          </Routes>
        </ErrorBoundary>
      </main>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <AppLayout />
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
